import React from "react";
import { Send, Pencil, X, CheckCircle, FileCheck } from "lucide-react";
import { Shipment } from "../../types";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { validateEftiFields } from "../../utils/eftiValidation";

interface FormActionsProps {
  isEditMode: boolean;
  readonly: boolean;
  loading: boolean;
  shipment?: Shipment;
  shipmentType?: "International" | "Domestic";
  transportMode?: "ROAD" | "SEA" | "AIR";
  handleUnlockFields: () => void;
  handleLockFields: () => void;
  onFormSubmit: () => void;
  onValidateCmr: () => void;
  onValidateEfti: () => void;
  isWayBillCreated: boolean;
  setIsWayBillCreated: React.Dispatch<React.SetStateAction<boolean>>;
  WayBillLoading: boolean;
  dispatch: any;
  createWayBill: any;
  handleAssignClick: () => void;
  isAssignedLocal: boolean;
}

export const FormActions: React.FC<FormActionsProps> = ({
  isEditMode,
  readonly,
  loading,
  shipment,
  shipmentType,
  transportMode = "ROAD",
  handleUnlockFields,
  handleLockFields,
  onValidateCmr,
  onValidateEfti,
  isWayBillCreated,
  setIsWayBillCreated,
  WayBillLoading,
  createWayBill,
  dispatch,
}) => {
  const { t } = useTranslation("shipments");

  const handleCreateWayBill = async () => {
    const confirm = window.confirm(t("form.confirmSendEfti"));
    if (!confirm) return;
    setIsWayBillCreated(false);

    // Send eFti is only available in readonly mode (the form isn't mounted,
    // so setError() on the form has no UI to render against). We surface
    // problems as a bulleted JSX toast instead of a comma-separated string.
    const errors = validateEftiFields(shipment, t);
    if (errors.length) {
      toast.error(
        <div>
          <div className="font-semibold mb-1">
            {t("form.validationFailed").replace(/[:\s]+$/, "")}
          </div>
          <ul className="list-disc pl-5 space-y-0.5 text-sm leading-snug">
            {errors.map((e, i) => (
              <li key={`${e.path}-${i}`}>{e.message}</li>
            ))}
          </ul>
        </div>,
        { autoClose: 10000 },
      );
      return;
    }

    if (!shipment?._id) {
      toast.error(t("form.shipmentIdMissing"));
      return;
    }

    try {
      await dispatch(createWayBill(shipment._id)).unwrap();
      toast.success(t("form.eftiSentSuccess"));
      setIsWayBillCreated(true);
    } catch (error) {
      setIsWayBillCreated(false);
      console.error("eFti creation error:", error);
    }
  };

  // Show Validate CMR whenever the form is editable (create-mode, or edit-mode
  // with the form unlocked). Hidden when the form is locked to the driver app.
  const canValidateCmr =
    (!isEditMode || (isEditMode && !readonly)) &&
    !shipment?.driverAccess?.locked;

  // The validation button is labelled after the transport document the mode
  // produces: Sea → Bill of Lading, Air → Air Waybill, Road → CMR
  // (International) or Delivery Note (Domestic).
  const validateLabel =
    transportMode === "SEA"
      ? t("form.validateBillOfLading")
      : transportMode === "AIR"
        ? t("form.validateAirWaybill")
        : (shipmentType ?? shipment?.type) === "Domestic"
          ? t("form.validateDeliveryNote")
          : t("form.validateCmr");

  return (
    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t-2 border-gray-200 bg-white shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
      {/* ── Secondary / destructive actions (left-weighted) ── */}
      {isEditMode && readonly && !shipment?.driverAccess?.locked && (
        <button
          type="button"
          onClick={handleUnlockFields}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-gray-300 bg-white text-gray-700 text-sm font-semibold hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100 transition-colors"
        >
          <Pencil className="h-4 w-4" />
          {t("form.edit")}
        </button>
      )}

      {isEditMode && !readonly && !shipment?.driverAccess?.locked && (
        <button
          type="button"
          onClick={handleLockFields}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-gray-300 bg-white text-gray-700 text-sm font-semibold hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100 transition-colors"
        >
          <X className="h-4 w-4" />
          {t("form.cancel")}
        </button>
      )}

      {/* ── Validate document: CMR for International, Delivery Note for Domestic ── */}
      {canValidateCmr && (
        <button
          type="button"
          onClick={onValidateCmr}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-gray-300 bg-white text-gray-700 text-sm font-semibold hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100 transition-colors"
        >
          <FileCheck className="h-4 w-4" />
          {validateLabel}
        </button>
      )}

      {/* ── Validate eFti — runs against live form values; surfaces field-level errors.
            eFTI is an inland-transport regime — ROAD only, hidden for SEA/AIR. ── */}
      {isEditMode &&
        !readonly &&
        transportMode === "ROAD" &&
        shipment?.type === "International" && (
          <button
            type="button"
            onClick={onValidateEfti}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-gray-300 bg-white text-gray-700 text-sm font-semibold hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100 transition-colors"
          >
            <CheckCircle className="h-4 w-4" />
            {t("form.validateEfti")}
          </button>
        )}

      {/* ── Send eFti — inland-only, ROAD shipments only ── */}
      {shipment &&
        shipment.type === "International" &&
        (shipment.transportMode || "ROAD") === "ROAD" &&
        shipment.waybill?.status !== "submitted" &&
        !isWayBillCreated &&
        readonly && (
          <button
            type="button"
            onClick={handleCreateWayBill}
            disabled={WayBillLoading}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-white text-sm font-bold tracking-wide shadow-md hover:bg-primary-700 active:bg-primary-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
          >
            {WayBillLoading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                {t("form.submitting")}
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                {t("form.sendEfti")}
              </>
            )}
          </button>
        )}

      {/* ── Primary: Create / Update ── */}
      {(!isEditMode || (isEditMode && !readonly)) &&
        !shipment?.driverAccess?.locked && (
          <button
            type="submit"
            form="shipment-form"
            disabled={loading}
            className="inline-flex items-center gap-2 px-8 py-2.5 rounded-lg bg-primary text-white text-sm font-bold tracking-wide shadow-md hover:bg-primary-700 active:bg-primary-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                {t("form.saving")}
              </>
            ) : isEditMode ? (
              t("form.update")
            ) : (
              t("form.create")
            )}
          </button>
        )}
    </div>
  );
};
